import type { CommentVisibility } from '@moko/utils';

export type CommentRole = 'managers' | 'client' | 'contractor';

export const COMMENT_ROLE_OPTIONS: { value: CommentRole; label: string }[] = [
  { value: 'managers', label: 'Managers' },
  { value: 'client', label: 'Client' },
  { value: 'contractor', label: 'Contractor' },
];

const ROLE_LABEL = new Map<CommentRole, string>(
  COMMENT_ROLE_OPTIONS.map((o) => [o.value, o.label])
);

const ALL_ROLES: CommentRole[] = COMMENT_ROLE_OPTIONS.map((o) => o.value);

const isRole = (value: string): value is CommentRole =>
  ALL_ROLES.includes(value as CommentRole);

export interface CommentVisibilityForm {
  isPublic: boolean;
  roles: CommentRole[];
}

export const DEFAULT_VISIBILITY_FORM: CommentVisibilityForm = {
  isPublic: true,
  roles: ['managers'],
};

export const parseVisibility = (
  value: CommentVisibility | string | undefined
): CommentVisibilityForm => {
  if (!value || value === 'private') return { isPublic: false, roles: [] };
  const roles = value
    .split(',')
    .map((v) => v.trim())
    .filter(isRole);
  return { isPublic: true, roles };
};

export const serializeVisibility = (form: CommentVisibilityForm): string =>
  form.isPublic ? form.roles.join(',') : 'private';

export const roleLabels = (roles: CommentRole[]): string =>
  roles.map((r) => ROLE_LABEL.get(r) ?? r).join(', ');

export const publicRoleLabels = (
  value: CommentVisibility | string | undefined
): string => roleLabels(parseVisibility(value).roles);

export const isPrivateVisibility = (
  value: CommentVisibility | string | undefined
): boolean => parseVisibility(value).isPublic === false;
